// src/components/scratch/ScoreHistoryChart.jsx
import React from "react";
import { formatDate } from "../../services/scratchService";

/**
 * Sparkline of the Scratch Score over time, drawn as inline SVG.
 * Expects ScratchScorePointDto[] (oldest → newest or newest → oldest; we sort).
 * Score axis is fixed 0..100 so the slope is comparable across games.
 */
export default function ScoreHistoryChart({ points = [], height = 120 }) {
  const data = [...points]
    .filter((p) => p && p.snapshotDate != null && p.scratchScore != null)
    .sort((a, b) => String(a.snapshotDate).localeCompare(String(b.snapshotDate)));

  if (data.length < 2) {
    return (
      <p className="text-[12px]" style={{ color: "var(--txt-3)" }}>
        Not enough history yet — the score trend appears after a few daily updates.
      </p>
    );
  }

  const width = 520;
  const padX = 8;
  const padY = 10;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const x = (i) => padX + (innerW * i) / (data.length - 1);
  const y = (score) => padY + innerH * (1 - Math.max(0, Math.min(100, Number(score))) / 100);

  const linePath = data
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.scratchScore).toFixed(1)}`)
    .join(" ");

  const areaPath =
    `${linePath} L ${x(data.length - 1).toFixed(1)} ${(padY + innerH).toFixed(1)}` +
    ` L ${x(0).toFixed(1)} ${(padY + innerH).toFixed(1)} Z`;

  const first = data[0];
  const last = data[data.length - 1];
  const delta = Number(last.scratchScore) - Number(first.scratchScore);
  const tone = delta > 1 ? "var(--gold)" : delta < -1 ? "var(--red)" : "var(--blue)";

  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <svg width="100%" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Scratch Score history">
          {[0, 25, 50, 75, 100].map((g) => (
            <line
              key={g}
              x1={padX}
              x2={width - padX}
              y1={y(g)}
              y2={y(g)}
              stroke="var(--border)"
              strokeWidth={1}
              strokeDasharray="3 4"
            />
          ))}
          <path d={areaPath} fill={tone} opacity={0.12} />
          <path d={linePath} fill="none" stroke={tone} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
          {data.map((p, i) => (
            <circle key={i} cx={x(i)} cy={y(p.scratchScore)} r={2.5} fill={tone} />
          ))}
        </svg>
      </div>
      <div className="flex items-center justify-between mt-1 text-[11px]" style={{ color: "var(--txt-3)" }}>
        <span>{formatDate(first.snapshotDate)}</span>
        <span style={{ color: tone }}>
          {delta > 0 ? "+" : ""}
          {delta.toFixed(0)} pts
        </span>
        <span>{formatDate(last.snapshotDate)}</span>
      </div>
    </div>
  );
}
